import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiCreditCard,
  FiGlobe,
  FiMail,
  FiShield,
  FiUser,
  FiUsers
} from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';
import './SubscriptionPage.css';

const copy = {
  en: {
    back: 'Back',
    brandSmall: 'Patient Simulator',
    title: 'Choose a subscription',
    subtitle: 'Select individual access or organize training for a class, clinic, or study group.',
    individual: 'Individual',
    organization: 'Organization',
    individualHelp: 'For one learner practicing independently.',
    organizationHelp: 'For cohorts, instructors, and institutional review.',
    choosePlan: 'Choose a plan',
    billingAddress: 'Billing address',
    ready: 'Ready to connect payment',
    selected: 'Selected',
    billedOnce: 'Single purchase for the selected period. No automatic renewal is enabled yet.',
    support: 'Need help with access? Contact support@medsim.local',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Billing email',
    organizationName: 'Organization name',
    address1: 'Address line 1',
    address2: 'Address line 2 (optional)',
    city: 'City',
    state: 'State / region',
    zip: 'Zip / postal code',
    country: 'Country',
    next: 'Next',
    submit: 'Save billing details',
    paymentNote: 'Payment gateway is not connected in this demo. This page is prepared for checkout integration.',
    individualPlans: [
      { id: 'student-monthly', name: 'Student Monthly', price: '$9', period: 'per month', badge: 'Flexible', features: ['All active cases', 'AI feedback after every encounter', 'Saved transcripts'] },
      { id: 'student-yearly', name: 'Student Yearly', price: '$90', period: 'per year', badge: 'Best value', featured: true, features: ['Two months included', 'All active cases', 'Multilingual practice'] }
    ],
    organizationPlans: [
      { id: 'cohort', name: 'Cohort', price: '$29', period: 'per seat / month', badge: 'Groups', featured: true, features: ['Instructor dashboard', 'Student submission review', 'Shared case library'] },
      { id: 'institution', name: 'Institution', price: 'Custom', period: 'annual agreement', badge: 'Contact us', features: ['Custom seats', 'Onboarding support', 'Priority case updates'] }
    ]
  },
  ru: {
    back: 'Назад',
    brandSmall: 'Симулятор пациентов',
    title: 'Выберите подписку',
    subtitle: 'Выберите индивидуальный доступ или обучение для группы, клиники или учебного центра.',
    individual: 'Индивидуально',
    organization: 'Организация',
    individualHelp: 'Для одного учащегося.',
    organizationHelp: 'Для групп, преподавателей и проверки работ.',
    choosePlan: 'Выберите план',
    billingAddress: 'Платёжный адрес',
    ready: 'Готово к подключению оплаты',
    selected: 'Выбрано',
    billedOnce: 'Разовая покупка на выбранный период. Автопродление пока не включено.',
    support: 'Нужна помощь с доступом? support@medsim.local',
    firstName: 'Имя',
    lastName: 'Фамилия',
    email: 'Email для оплаты',
    organizationName: 'Название организации',
    address1: 'Адрес, строка 1',
    address2: 'Адрес, строка 2 (необязательно)',
    city: 'Город',
    state: 'Область / регион',
    zip: 'Индекс',
    country: 'Страна',
    next: 'Далее',
    submit: 'Сохранить платёжные данные',
    paymentNote: 'Платёжный шлюз в демо не подключён. Страница подготовлена для интеграции checkout.',
    individualPlans: [
      { id: 'student-monthly', name: 'Студент месяц', price: '$9', period: 'в месяц', badge: 'Гибко', features: ['Все активные случаи', 'ИИ-разбор после каждого приёма', 'Сохранённые переписки'] },
      { id: 'student-yearly', name: 'Студент год', price: '$90', period: 'в год', badge: 'Выгодно', featured: true, features: ['Два месяца включены', 'Все активные случаи', 'Многоязычная практика'] }
    ],
    organizationPlans: [
      { id: 'cohort', name: 'Группа', price: '$29', period: 'за место / месяц', badge: 'Группы', featured: true, features: ['Панель преподавателя', 'Проверка работ студентов', 'Общая библиотека случаев'] },
      { id: 'institution', name: 'Учреждение', price: 'Custom', period: 'годовой договор', badge: 'Связаться', features: ['Индивидуальное число мест', 'Помощь с запуском', 'Приоритетные обновления случаев'] }
    ]
  },
  uz: {
    back: 'Orqaga',
    brandSmall: 'Bemor simulyatori',
    title: 'Obunani tanlang',
    subtitle: 'Yakka tartibdagi kirish yoki guruh, klinika va o‘quv markazi uchun rejani tanlang.',
    individual: 'Individual',
    organization: 'Tashkilot',
    individualHelp: 'Mustaqil mashq qiladigan bitta talaba uchun.',
    organizationHelp: 'Guruhlar, o‘qituvchilar va tekshiruv jarayoni uchun.',
    choosePlan: 'Rejani tanlang',
    billingAddress: 'To‘lov manzili',
    ready: 'To‘lov ulashga tayyor',
    selected: 'Tanlangan',
    billedOnce: 'Tanlangan muddat uchun bir martalik xarid. Avtomatik yangilanish hozircha yoqilmagan.',
    support: 'Kirish bo‘yicha yordam kerakmi? support@medsim.local',
    firstName: 'Ism',
    lastName: 'Familiya',
    email: 'To‘lov emaili',
    organizationName: 'Tashkilot nomi',
    address1: 'Manzil 1-qator',
    address2: 'Manzil 2-qator (ixtiyoriy)',
    city: 'Shahar',
    state: 'Viloyat / hudud',
    zip: 'Pochta indeksi',
    country: 'Mamlakat',
    next: 'Keyingi',
    submit: 'To‘lov ma’lumotlarini saqlash',
    paymentNote: 'Demo versiyada to‘lov tizimi ulanmagan. Sahifa checkout integratsiyasi uchun tayyor.',
    individualPlans: [
      { id: 'student-monthly', name: 'Talaba oylik', price: '$9', period: 'oyiga', badge: 'Moslashuvchan', features: ['Barcha faol holatlar', 'Har qabuldan keyin AI bahosi', 'Saqlangan suhbatlar'] },
      { id: 'student-yearly', name: 'Talaba yillik', price: '$90', period: 'yiliga', badge: 'Eng qulay', featured: true, features: ['Ikki oy qo‘shilgan', 'Barcha faol holatlar', 'Ko‘p tilli mashq'] }
    ],
    organizationPlans: [
      { id: 'cohort', name: 'Guruh', price: '$29', period: 'joy / oy', badge: 'Guruhlar', featured: true, features: ['O‘qituvchi paneli', 'Talaba ishlarini tekshirish', 'Umumiy holatlar kutubxonasi'] },
      { id: 'institution', name: 'Muassasa', price: 'Custom', period: 'yillik kelishuv', badge: 'Bog‘lanish', features: ['Maxsus joylar soni', 'Ishga tushirish yordami', 'Ustuvor holat yangilanishlari'] }
    ]
  }
};

const initialBilling = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: ''
};

function SubscriptionPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const text = copy[language] || copy.en;
  const [accountType, setAccountType] = useState('individual');
  const plans = accountType === 'organization' ? text.organizationPlans : text.individualPlans;
  const [selectedPlanId, setSelectedPlanId] = useState(plans.find(plan => plan.featured)?.id || plans[0].id);
  const [step, setStep] = useState('plans');
  const [billing, setBilling] = useState(initialBilling);

  const selectedPlan = useMemo(
    () => plans.find(plan => plan.id === selectedPlanId) || plans[0],
    [plans, selectedPlanId]
  );

  const changeType = (type) => {
    const nextPlans = type === 'organization' ? text.organizationPlans : text.individualPlans;
    setAccountType(type);
    setSelectedPlanId(nextPlans.find(plan => plan.featured)?.id || nextPlans[0].id);
    setStep('plans');
  };

  const updateBilling = (event) => {
    const { name, value } = event.target;
    setBilling(prev => ({ ...prev, [name]: value }));
  };

  return (
    <main className="subscription-page">
      <header className="subscription-header">
        <button type="button" className="subscription-back" onClick={() => navigate(-1)}>
          <FiArrowLeft size={18} />
          <span>{text.back}</span>
        </button>
        <button type="button" className="subscription-brand" onClick={() => navigate('/')}>
          <strong>MedSim</strong>
          <small>{text.brandSmall}</small>
        </button>
      </header>

      <section className="subscription-shell">
        <div className="subscription-intro">
          <span className="subscription-kicker">{text.ready}</span>
          <h1>{text.title}</h1>
          <p>{text.subtitle}</p>
        </div>

        <div className="subscription-stepper" aria-label="Subscription steps">
          <span className={step === 'plans' ? 'active' : ''} />
          <span className={step === 'billing' ? 'active' : ''} />
        </div>

        <div className="account-toggle" role="tablist" aria-label="Subscription type">
          <button type="button" className={accountType === 'individual' ? 'active' : ''} onClick={() => changeType('individual')}>
            <FiUser size={20} />
            <span><strong>{text.individual}</strong><small>{text.individualHelp}</small></span>
          </button>
          <button type="button" className={accountType === 'organization' ? 'active' : ''} onClick={() => changeType('organization')}>
            <FiUsers size={20} />
            <span><strong>{text.organization}</strong><small>{text.organizationHelp}</small></span>
          </button>
        </div>

        {step === 'plans' ? (
          <section className="subscription-panel">
            <div className="panel-title-row">
              <h2>{text.choosePlan}</h2>
              <span><FiShield size={16} /> {text.billedOnce}</span>
            </div>
            <div className="paywall-plans">
              {plans.map(plan => (
                <button
                  type="button"
                  key={plan.id}
                  className={`paywall-card ${plan.featured ? 'featured' : ''} ${selectedPlanId === plan.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPlanId(plan.id)}
                >
                  <span className="paywall-badge">{plan.badge}</span>
                  <h3>{plan.name}</h3>
                  <strong>{plan.price}</strong>
                  <small>{plan.period}</small>
                  <ul>
                    {plan.features.map(feature => <li key={feature}><FiCheck size={16} /> {feature}</li>)}
                  </ul>
                </button>
              ))}
            </div>
            <button type="button" className="subscription-primary" onClick={() => setStep('billing')}>
              <FiCreditCard size={18} />
              <span>{text.next}</span>
            </button>
          </section>
        ) : (
          <section className="subscription-panel billing-panel">
            <div className="order-summary">
              <span>{selectedPlan.name}</span>
              <strong>{selectedPlan.price}</strong>
            </div>
            <h2>{text.billingAddress}</h2>
            <form className="billing-form" onSubmit={(event) => event.preventDefault()}>
              <input name="firstName" value={billing.firstName} onChange={updateBilling} placeholder={text.firstName} />
              <input name="lastName" value={billing.lastName} onChange={updateBilling} placeholder={text.lastName} />
              <input className="wide" name="email" value={billing.email} onChange={updateBilling} placeholder={text.email} type="email" />
              {accountType === 'organization' && (
                <input className="wide" name="organizationName" value={billing.organizationName} onChange={updateBilling} placeholder={text.organizationName} />
              )}
              <input className="wide" name="address1" value={billing.address1} onChange={updateBilling} placeholder={text.address1} />
              <input className="wide" name="address2" value={billing.address2} onChange={updateBilling} placeholder={text.address2} />
              <input name="city" value={billing.city} onChange={updateBilling} placeholder={text.city} />
              <input name="zip" value={billing.zip} onChange={updateBilling} placeholder={text.zip} />
              <input name="state" value={billing.state} onChange={updateBilling} placeholder={text.state} />
              <label className="country-select">
                <FiGlobe size={18} />
                <select name="country" value={billing.country} onChange={updateBilling}>
                  <option value="">{text.country}</option>
                  <option>Uzbekistan</option>
                  <option>United States</option>
                  <option>United Kingdom</option>
                  <option>Turkey</option>
                  <option>Kazakhstan</option>
                </select>
              </label>
            </form>
            <button type="button" className="subscription-primary" onClick={() => setStep('plans')}>
              <FiCheck size={18} />
              <span>{text.submit}</span>
            </button>
          </section>
        )}

        <footer className="subscription-support">
          <FiMail size={18} />
          <span>{text.support}</span>
        </footer>
        <p className="subscription-note">{text.paymentNote}</p>
      </section>
    </main>
  );
}

export default SubscriptionPage;
